import React, { useState } from "react";
import {
  AvailableIngredientGroup,
  BlueprintSection,
  CustomContentRow,
} from "../../types/contentTools";

interface SectionsPanelProps {
  sections: BlueprintSection[];
  customContentRows: CustomContentRow[];
  availableGroups: AvailableIngredientGroup[];
  onLinkTemplate: (
    sectionIndex: number,
    templateId: string | null,
  ) => Promise<void>;
  onUpdateGroup: (
    sectionIndex: number,
    orgIngredientGroupId: string | null,
  ) => Promise<void>;
  onUpdateConfig: (
    sectionIndex: number,
    config: {
      instructions?: string;
      required_articles_count?: number;
      source_selection_mode?: string;
    },
  ) => Promise<void>;
}

const SOURCE_MODES = ["any", "original_only", "external_only"] as const;

/**
 * Per-section NL-automation configuration: link a custom content row template
 * and an ingredient group to each blueprint section, and edit the sourcing
 * knobs (instructions / required article count / source selection mode).
 */
const SectionsPanel: React.FC<SectionsPanelProps> = ({
  sections,
  customContentRows,
  availableGroups,
  onLinkTemplate,
  onUpdateGroup,
  onUpdateConfig,
}) => {
  const [pending, setPending] = useState<Set<string>>(new Set());
  const [notice, setNotice] = useState<{
    key: string;
    type: "success" | "error";
    text: string;
  } | null>(null);

  const run = (key: string, fn: () => Promise<void>) => {
    setPending((prev) => new Set(prev).add(key));
    setNotice(null);
    fn()
      .then(() => setNotice({ key, type: "success", text: "Saved." }))
      .catch((e: any) =>
        setNotice({ key, type: "error", text: e?.message || "Failed to save." }),
      )
      .finally(() =>
        setPending((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        }),
      );
  };

  if (sections.length === 0) {
    return (
      <div style={{ color: "#718096", fontSize: "13px" }}>
        No sections defined for this blueprint.
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {sections.map((section, index) => {
        const templateValue =
          section.custom_content_rows_template_id != null
            ? String(section.custom_content_rows_template_id)
            : "";
        const groupValue =
          section.organization_ingredient_group_id != null
            ? String(section.organization_ingredient_group_id)
            : "";
        const baseFormat = section.format === "link_dump" ? "Link Dump" : "Summary";

        return (
          <div
            key={index}
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: "6px",
              padding: "16px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: "12px",
                marginBottom: "12px",
              }}
            >
              <span style={{ fontWeight: 600, fontSize: "15px", color: "#2D3748" }}>
                {section.title}
              </span>
              <span style={{ fontSize: "12px", color: "#718096" }}>
                Base format: {baseFormat} · {section.content_type || "—"}
              </span>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "16px",
              }}
            >
              <label style={labelStyle}>
                Custom template
                <select
                  value={templateValue}
                  disabled={pending.has(`${index}:template`)}
                  onChange={(e) =>
                    run(`${index}:template`, () =>
                      onLinkTemplate(index, e.target.value || null),
                    )
                  }
                  style={inputStyle}
                >
                  <option value="">— none (base format) —</option>
                  {customContentRows.map((c) => (
                    <option key={String(c.id)} value={String(c.id)}>
                      {c.title}
                    </option>
                  ))}
                </select>
              </label>

              <label style={labelStyle}>
                Ingredient group
                <select
                  value={groupValue}
                  disabled={pending.has(`${index}:group`)}
                  onChange={(e) =>
                    run(`${index}:group`, () =>
                      onUpdateGroup(index, e.target.value || null),
                    )
                  }
                  style={inputStyle}
                >
                  <option value="">— none —</option>
                  {availableGroups.map((g) => (
                    <option
                      key={String(g.org_ingredient_group_id)}
                      value={String(g.org_ingredient_group_id)}
                    >
                      {g.group_name} ({g.total_available})
                    </option>
                  ))}
                </select>
              </label>

              <label style={{ ...labelStyle, gridColumn: "1 / -1" }}>
                Instructions
                <textarea
                  defaultValue={section.instructions}
                  disabled={pending.has(`${index}:instructions`)}
                  onBlur={(e) =>
                    run(`${index}:instructions`, () =>
                      onUpdateConfig(index, { instructions: e.target.value }),
                    )
                  }
                  style={{ ...inputStyle, height: "72px", resize: "vertical" }}
                />
              </label>

              <label style={labelStyle}>
                Required articles (0 = optional)
                <input
                  type="number"
                  min={0}
                  step={1}
                  defaultValue={section.required_articles_count}
                  disabled={pending.has(`${index}:count`)}
                  onBlur={(e) => {
                    const raw = e.target.value.trim();
                    const parsed = Number(raw);
                    if (raw === "" || Number.isNaN(parsed) || parsed < 0) {
                      setNotice({
                        key: `${index}:count`,
                        type: "error",
                        text: "Must be a whole number >= 0.",
                      });
                      return;
                    }
                    run(`${index}:count`, () =>
                      onUpdateConfig(index, {
                        required_articles_count: Math.floor(parsed),
                      }),
                    );
                  }}
                  style={inputStyle}
                />
              </label>

              <label style={labelStyle}>
                Source mode
                <select
                  value={section.source_selection_mode}
                  disabled={pending.has(`${index}:mode`)}
                  onChange={(e) =>
                    run(`${index}:mode`, () =>
                      onUpdateConfig(index, {
                        source_selection_mode: e.target.value,
                      }),
                    )
                  }
                  style={inputStyle}
                >
                  {SOURCE_MODES.map((mode) => (
                    <option key={mode} value={mode}>
                      {mode}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div style={{ marginTop: "12px", fontSize: "12px", color: "#718096" }}>
              Preferred sources:{" "}
              {section.preferred_sources.length > 0
                ? section.preferred_sources.join(", ")
                : "— (managed on the Sources page)"}
            </div>

            {notice && notice.key.startsWith(`${index}:`) && (
              <div
                style={{
                  marginTop: "8px",
                  fontSize: "12px",
                  color: notice.type === "success" ? "#2F855A" : "#C53030",
                }}
              >
                {notice.text}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const labelStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  fontSize: "13px",
  fontWeight: 600,
  color: "#4A5568",
};

const inputStyle: React.CSSProperties = {
  padding: "6px 10px",
  border: "1px solid #e2e8f0",
  borderRadius: "4px",
  fontSize: "14px",
  boxSizing: "border-box",
  width: "100%",
};

export default SectionsPanel;
